import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { IValidator } from 'interfaces/validator';
import { resetPassword } from 'services/auth';
import YM from 'utils/metrics';
import { validateValue, validateForm } from 'utils/validation';
import Button from 'components/Button';
import Card from 'components/Card';
import InputPassword from 'components/Input/InputPassword';
import ModalError from 'components/Modals/ModalError';

import classes from './resetPassword.module.scss';

const ForgotPassword: React.FC = () => {
	const navigate = useNavigate();
	const { resetToken = '' } = useParams();
	const [passwordData, setPasswordData] = useState({
		password: '',
		confirmPassword: '',
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({
		password: '',
		confirmPassword: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

	const validateFieldValue = ({ type, value }: IValidator): void => {
		const isConfirmPassword = type === 'confirmPassword';
		const error = validateValue({
			type,
			value,
			isRequired: true,
			confirmValue: isConfirmPassword ? passwordData.password : '',
		});
		setErrors({ ...errors, [type]: error });
	};

	const handleChange =
		(type: string) =>
		(value: string): void => {
			setPasswordData({ ...passwordData, [type]: value });
			validateFieldValue({ type, value });
		};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		if (isLoading) return;

		const { isValid, formErrors } = validateForm({
			formData: passwordData,
			isRequired: true,
		});
		if (!isValid) {
			return setErrors(formErrors);
		}

		setIsLoading(true);
		try {
			await resetPassword({ ...passwordData, resetToken });
			YM.onFormSubmit();
			navigate('../login');
		} catch (e) {
			setIsErrorModalOpen(true);
		}
		setIsLoading(false);
	};

	const handleToggleErrorModal = (): void => {
		setIsErrorModalOpen(!isErrorModalOpen);
	};

	const formInputs = (
		<div className={classes.formInputs}>
			<InputPassword
				className={classes.field}
				value={passwordData.password}
				onChange={handleChange('password')}
				placeholder="Задайте пароль"
				error={errors.password}
			/>
			<InputPassword
				className={classes.field}
				value={passwordData.confirmPassword}
				onChange={handleChange('confirmPassword')}
				placeholder="Повторный ввод пароля"
				error={errors.confirmPassword}
			/>
		</div>
	);

	const formButtons = (
		<div className={classes.formButtons}>
			<Button
				className={classes.button}
				text="Сохранить новый пароль"
				type="submit"
				isLoading={isLoading}
			/>
			<div className={classes.separator}>
				<div className={classes.separatorText}>Или</div>
			</div>
			<Button
				className={classes.buttonLink}
				text="Войти"
				href="../login"
				styleType="secondary"
			/>
		</div>
	);

	const form = (
		<form className={classes.form} onSubmit={handleSubmit}>
			{formInputs}
			{formButtons}
		</form>
	);

	return (
		<Card
			className={classes.resetPassword}
			title="Пароль учетной записи"
			titleClassName={classes.title}
		>
			{form}
			<ModalError
				className={classes.modal}
				isOpen={isErrorModalOpen}
				onClose={handleToggleErrorModal}
				text="Пользователь не найден"
			/>
		</Card>
	);
};

export default ForgotPassword;
